import type {HttpUserPrompt} from '@wandb/weave/lib/WbAgent/promptParts';
import type {
  TurnResponse,
  TurnState,
  WbAgentClient,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {TERMINAL_STATES} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import {getNextTurnPermissions} from './devPermissions';
import {extractErrorMessage} from './errors';
import type {RuntimeEvent} from './events';
import type {AgentRuntime, ResumeTurnParams, RunTurnParams} from './runtime';
import {
  extractTurnId,
  terminalEventForTurnState,
  TurnEventExtractor,
} from './turn';

export type GetParentNodeId = (nodeId: string) => string | null;

// Backend's auto-created catch-all project for turns created without scope.
// Surfaced as a real project on responses but not meaningful to label.
export const DEFAULT_PROJECT_NAME = 'wandb_agent_default_project';

const POLL_INITIAL_MS = 1000;
const POLL_SETTLED_MS = 3000;
const POLL_MAX_MS = 5000;
const POLL_FACTOR = 1.5;

/**
 * ThreadOperation holds a thread's polling `controller` and its `turnId` once
 * known, so an explicit `cancel()` can reach the turn server-side.
 */
type ThreadOperation = {controller: AbortController; turnId: string | null};

/**
 * Poll-based runtime backed by the wb_agent service.
 *
 * Creates turns via `POST /turns`, `polls GET /turns/{id}` with adaptive
 * backoff, and synthesizes {@link RuntimeEvent}s from turn snapshots.
 *
 * Aborting a signal only stops polling; `cancel()` is the sole path that
 * cancels a turn server-side.
 */
export class WBAgentRuntime implements AgentRuntime {
  private operations = new Map<string, ThreadOperation>();

  constructor(
    private readonly client: WbAgentClient,
    private readonly getParentNodeId: GetParentNodeId
  ) {}

  cancel(threadId: string): void {
    const op = this.operations.get(threadId);
    if (!op) {
      return;
    }

    if (op.turnId != null) {
      void this.cancelTurn(op.turnId);
    }

    op.controller.abort();
    this.operations.delete(threadId);
  }

  async *resumeTurn({
    turnId,
    threadId,
    signal: externalSignal,
    initialSnapshot,
  }: ResumeTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const {signal, setTurnId, finish} = this.startOperation(
      threadId,
      externalSignal
    );

    // Register the resumed turn so an explicit cancel can reach it.
    setTurnId(turnId);

    const runId = crypto.randomUUID();
    yield {type: 'run-started', runId, threadId};

    const extractor = new TurnEventExtractor(turnId);
    extractor.extract(initialSnapshot);

    try {
      if (isTerminal(initialSnapshot.state)) {
        const event = terminalEventForTurnState(
          initialSnapshot.state,
          runId,
          initialSnapshot.error_info
        );
        if (event) {
          yield event;
        }

        return;
      }

      yield* this.pollTurn(turnId, runId, signal, extractor);
    } catch (err) {
      // Abort stops polling silently; surface only real errors.
      if (!signal.aborted) {
        yield {type: 'run-error', message: extractErrorMessage(err), runId};
      }
    } finally {
      finish();
    }
  }

  async *runTurn({
    threadId,
    userPrompt,
    parentId,
    threadRootTurnId,
    signal: externalSignal,
  }: RunTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const {signal, rekey, setTurnId, finish} = this.startOperation(
      threadId,
      externalSignal
    );

    // NOTE: Client-only; wb_agent doesn't have run IDs. This helps uniquely
    // identify conversational turns within the frontend chat state.
    const runId = crypto.randomUUID();
    yield {type: 'run-started', runId, threadId};

    try {
      const result = await this.createTurn(
        userPrompt ?? '',
        parentId ?? null,
        threadRootTurnId ?? null,
        signal
      );
      if (!result.ok) {
        return;
      }

      // Register the turn so an explicit cancel can reach it. The thread id
      // rotates from the client UUID to `rootTurnId` on promotion (in the
      // store, on this same `turn-created`); move the controller with it so a
      // later `cancel(rootTurnId)` from the stop button reaches it.
      setTurnId(result.turnId);
      rekey(result.rootTurnId);
      yield {type: 'turn-created', rootTurnId: result.rootTurnId};

      if (signal.aborted) {
        return;
      }

      yield* this.pollTurn(result.turnId, runId, signal);
    } catch (err) {
      // Abort stops polling silently; surface only real errors.
      if (!signal.aborted) {
        yield {type: 'run-error', message: extractErrorMessage(err), runId};
      }
    } finally {
      finish();
    }
  }

  /**
   * startOperation supersedes any in-flight operation for the thread (stopping
   * its polling, not canceling its turn). Returns the abort signal, a
   * `setTurnId` to register the turn so `cancel()` can reach it, a `rekey` that
   * moves the operation to the turn's `rootTurnId` once known (so a later
   * `cancel(rootTurnId)` reaches it after the thread id is promoted), and a
   * `finish` that drops the operation unless a newer one replaced it.
   */
  private startOperation(
    threadId: string,
    externalSignal: AbortSignal
  ): {
    signal: AbortSignal;
    setTurnId: (turnId: string) => void;
    rekey: (newKey: string) => void;
    finish: () => void;
  } {
    this.operations.get(threadId)?.controller.abort();

    const controller = new AbortController();
    const op: ThreadOperation = {controller, turnId: null};

    // Keyed by the caller's thread id, which for a new chat is a client UUID
    // that rotates to `rootTurnId` on promotion; `rekey` keeps the key current.
    let currentKey = threadId;
    this.operations.set(currentKey, op);

    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(), {
        once: true,
      });
    }

    return {
      signal: controller.signal,
      setTurnId: (turnId: string) => {
        op.turnId = turnId;
      },
      rekey: (newKey: string) => {
        // No-op when the key is unchanged (a follow-up turn's rootTurnId already
        // equals the thread id), or when the operation already wound down.
        if (newKey === currentKey || this.operations.get(currentKey) !== op) {
          return;
        }

        this.operations.delete(currentKey);
        this.operations.set(newKey, op);

        currentKey = newKey;
      },
      finish: () => {
        if (this.operations.get(currentKey) === op) {
          this.operations.delete(currentKey);
        }
      },
    };
  }

  private async createTurn(
    userPrompt: HttpUserPrompt,
    parentId: string | null,
    threadRootTurnId: string | null,
    signal: AbortSignal
  ): Promise<{ok: true; turnId: string; rootTurnId: string} | {ok: false}> {
    const parentTurnId = this.resolveParentTurnId(parentId) ?? threadRootTurnId;

    if (signal.aborted) {
      return {ok: false};
    }

    const turnResponse = await this.client.post(
      '/turns',
      {
        // entity/project only scope the root turn; child turns inherit from
        // the root, so omit them when a parent_turn_id is set.
        ...(parentTurnId == null
          ? {entity: this.client.entity, project: this.client.project}
          : {}),
        parent_turn_id: parentTurnId,
        user_prompt: userPrompt ?? '',
        permissions: getNextTurnPermissions(),
      },
      undefined,
      signal
    );

    return {
      ok: true,
      rootTurnId: turnResponse.root_turn_id,
      turnId: turnResponse.id,
    };
  }

  /**
   * Polls `GET /turns/{id}` with adaptive backoff until a terminal state is
   * reached. Yields content events and terminal events. On abort, returns
   * silently; polling stops and the turn is left running server-side.
   */
  private async *pollTurn(
    turnId: string,
    runId: string,
    signal: AbortSignal,
    extractor?: TurnEventExtractor
  ): AsyncGenerator<RuntimeEvent, void, undefined> {
    extractor ??= new TurnEventExtractor(turnId, {
      // NOTE: User messages are optimistically added to thread state, so drop
      // them for in-progress turns.
      skipUserMessages: true,
    });

    let hasReceivedData = false;
    let interval = POLL_INITIAL_MS;
    let lastSnapshotKey = '';

    while (true) {
      if (signal.aborted) {
        break;
      }

      await sleep(interval, signal);
      if (signal.aborted) {
        break;
      }

      const turn = await this.client.get<TurnResponse>(
        `/turns/${turnId}`,
        undefined,
        signal
      );

      const snapshotKey = `${turn.state}:${(turn.messages ?? []).length}:${(turn.tool_calls ?? []).length}`;
      if (snapshotKey !== lastSnapshotKey) {
        lastSnapshotKey = snapshotKey;

        if (
          (turn.messages?.length ?? 0) > 0 ||
          (turn.tool_calls?.length ?? 0) > 0
        ) {
          hasReceivedData = true;
        }

        interval = hasReceivedData ? POLL_SETTLED_MS : POLL_INITIAL_MS;
      } else {
        interval = Math.max(
          Math.min(interval * POLL_FACTOR, POLL_MAX_MS),
          hasReceivedData ? POLL_SETTLED_MS : POLL_INITIAL_MS
        );
      }

      const events = extractor.extract(turn);
      for (const event of events) {
        yield event;
      }

      const terminalEvent = terminalEventForTurnState(
        turn.state,
        runId,
        turn.error_info
      );
      if (terminalEvent) {
        yield terminalEvent;
        return;
      }
    }
  }

  /**
   * resolveParentTurnId finds the WB agent turn ID that should be the parent of
   * a new turn anchored at `messageId`. Walks up via `getParentNodeId` until it
   * finds a node whose ID encodes a turn ID (format `${turnId}:<role>`).
   * Returns `null` if no parent turn can be found for the message; callers
   * typically fall back to the thread's `rootTurnId` so a continuation turn
   * still parents under the conversation (e.g. when the previous turn was
   * cancelled before any extractable nodes were created).
   *
   * NOTE: This function exists because optimistic user message nodes must use
   * internal IDs, not turn-encoded IDs; a turn doesn't exist for the user
   * message until the backend creates it and returns it.
   */
  private resolveParentTurnId(messageId: string | null): string | null {
    const visited = new Set<string>();

    let current = messageId;
    while (current && !visited.has(current)) {
      const turnId = extractTurnId(current);
      if (turnId) {
        return turnId;
      }

      visited.add(current);
      current = this.getParentNodeId(current);
    }

    return null;
  }

  private async cancelTurn(turnId: string): Promise<void> {
    try {
      await this.client.post<TurnResponse>(
        `/turns/${turnId}/cancel`,
        undefined
      );
    } catch {
      // Best-effort cancel; there's nothing to do here.
    }
  }
}

function isTerminal(state: TurnState): boolean {
  return TERMINAL_STATES.includes(state);
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };

    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal.addEventListener('abort', onAbort, {once: true});
  });
}
